import React, { useState } from 'react';
import Dialog from './Dialog';

interface HeadingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (type: 'paragraph' | 'heading', level?: 1 | 2 | 3 | 4 | 5 | 6) => void;
}

const HeadingDialog: React.FC<HeadingDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [selectedType, setSelectedType] = useState<'paragraph' | 'heading'>('paragraph');
  const [headingLevel, setHeadingLevel] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedType === 'heading') {
      onInsert('heading', headingLevel);
    } else {
      onInsert('paragraph');
    }
    onClose();
  };

  const headingOptions = [
    { level: 1, label: 'Heading 1 (H1)', description: 'Main title - largest heading' },
    { level: 2, label: 'Heading 2 (H2)', description: 'Section title - large heading' },
    { level: 3, label: 'Heading 3 (H3)', description: 'Subsection title - medium heading' },
    { level: 4, label: 'Heading 4 (H4)', description: 'Sub-subsection title - smaller heading' },
    { level: 5, label: 'Heading 5 (H5)', description: 'Minor section title - small heading' },
    { level: 6, label: 'Heading 6 (H6)', description: 'Smallest heading' },
  ];

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Insert Heading or Paragraph">
      <form onSubmit={handleSubmit} className="mj-heading-dialog-form">
        <div className="mj-form-group">
          <label>Select Type:</label>
          <div className="mj-radio-group">
            <label className="mj-radio-option">
              <input
                type="radio"
                name="type"
                value="paragraph"
                checked={selectedType === 'paragraph'}
                onChange={(e) => setSelectedType(e.target.value as 'paragraph' | 'heading')}
              />
              <span className="mj-radio-label">
                <strong>Paragraph (P)</strong>
                <span className="mj-radio-description">Regular paragraph text</span>
              </span>
            </label>
            
            <label className="mj-radio-option">
              <input
                type="radio"
                name="type"
                value="heading"
                checked={selectedType === 'heading'}
                onChange={(e) => setSelectedType(e.target.value as 'paragraph' | 'heading')}
              />
              <span className="mj-radio-label">
                <strong>Heading (H1-H6)</strong>
                <span className="mj-radio-description">Title or section heading</span>
              </span>
            </label>
          </div>
        </div>

        {selectedType === 'heading' && (
          <div className="mj-form-group">
            <label>Heading Level:</label>
            <div className="mj-heading-options">
              {headingOptions.map((option) => (
                <label 
                  key={option.level} 
                  className={`mj-heading-option ${headingLevel === option.level ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="headingLevel"
                    value={option.level}
                    checked={headingLevel === option.level}
                    onChange={(e) => setHeadingLevel(parseInt(e.target.value) as 1 | 2 | 3 | 4 | 5 | 6)}
                  />
                  <div className="mj-heading-preview">
                    <div className={`mj-heading-text h${option.level}`}>
                      {option.label}
                    </div>
                    <div className="mj-heading-description">
                      {option.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="mj-dialog-preview">
          <h4>Preview:</h4>
          <div className="mj-heading-preview-container">
            {selectedType === 'paragraph' ? (
              <p className="mj-preview-paragraph">
                This is how a paragraph will look. It contains regular text with normal formatting and spacing.
              </p>
            ) : (
              <div className={`mj-preview-heading h${headingLevel}`}>
                {`This is Heading ${headingLevel} (H${headingLevel})`}
              </div>
            )}
          </div>
        </div>

        <div className="mj-dialog-actions">
          <button type="button" onClick={onClose} className="mj-btn mj-btn-secondary">
            Cancel
          </button>
          <button type="submit" className="mj-btn mj-btn-primary">
            Insert {selectedType === 'paragraph' ? 'Paragraph' : `Heading ${headingLevel}`}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default HeadingDialog; 